import L from 'leaflet'
import { VECTOR_PANE } from './leaflet-constants.js'

/** Draw order on the shared canvas. Later bands paint and hit-test above earlier ones. */
export type VectorBand = 'polygon' | 'line' | 'circle'

const BAND_ORDER: readonly VectorBand[] = ['polygon', 'line', 'circle']

type LayerEntry = {
  root: L.Layer | null
  visible: boolean
  bands: Map<VectorBand, L.Layer[]>
}

export type LeafletRenderEngine = {
  vectorRenderer(): L.Canvas
  /** Layer group (or any layer) added and removed with visibility. */
  setRoot(layerId: string, root: L.Layer): void
  addPath(layerId: string, band: VectorBand, path: L.Layer): void
  setLayerVisible(layerId: string, visible: boolean): void
  clearLayer(layerId: string): void
  /** Re-apply band order after a layer group is shown again. */
  restack(): void
  destroy(): void
}

function ensurePane(map: L.Map, name: string, zIndex: string): void {
  if (!map.getPane(name)) {
    map.createPane(name)
    const pane = map.getPane(name)
    if (pane) pane.style.zIndex = zIndex
  }
}

/**
 * One `L.Canvas` for every Leaflet vector on the map.
 * Product layers stay virtual: the engine shows, hides, and stacks their paths.
 */
export function createLeafletRenderEngine(map: L.Map): LeafletRenderEngine {
  ensurePane(map, VECTOR_PANE, '450')
  const renderer = L.canvas({ pane: VECTOR_PANE })
  const layers = new Map<string, LayerEntry>()
  let orderQueued = false

  function entryFor(layerId: string): LayerEntry {
    let entry = layers.get(layerId)
    if (!entry) {
      entry = { root: null, visible: true, bands: new Map() }
      layers.set(layerId, entry)
    }
    return entry
  }

  function applyOrder(): void {
    orderQueued = false
    for (const band of BAND_ORDER) {
      for (const entry of layers.values()) {
        if (!entry.visible) continue
        const paths = entry.bands.get(band)
        if (!paths) continue
        for (const path of paths) {
          const front = path as L.Path
          if (map.hasLayer(path) && typeof front.bringToFront === 'function') {
            front.bringToFront()
          }
        }
      }
    }
  }

  function scheduleOrder(): void {
    if (orderQueued) return
    orderQueued = true
    queueMicrotask(applyOrder)
  }

  function showRoot(entry: LayerEntry): void {
    if (entry.root && !map.hasLayer(entry.root)) {
      entry.root.addTo(map)
    }
  }

  function hideRoot(entry: LayerEntry): void {
    if (entry.root && map.hasLayer(entry.root)) {
      map.removeLayer(entry.root)
    }
  }

  return {
    vectorRenderer() {
      return renderer
    },

    setRoot(layerId, root) {
      const entry = entryFor(layerId)
      entry.root = root
      if (entry.visible) showRoot(entry)
      scheduleOrder()
    },

    addPath(layerId, band, path) {
      const entry = entryFor(layerId)
      const list = entry.bands.get(band) ?? []
      if (!list.includes(path)) list.push(path)
      entry.bands.set(band, list)
      scheduleOrder()
    },

    setLayerVisible(layerId, visible) {
      const entry = layers.get(layerId)
      if (!entry) return
      entry.visible = visible
      if (visible) showRoot(entry)
      else hideRoot(entry)
      if (visible) scheduleOrder()
    },

    clearLayer(layerId) {
      const entry = layers.get(layerId)
      if (!entry) return
      hideRoot(entry)
      layers.delete(layerId)
    },

    restack() {
      scheduleOrder()
    },

    destroy() {
      for (const entry of layers.values()) hideRoot(entry)
      layers.clear()
      // Leave the canvas on the map. `removeLeafletMapSafely` no-ops an orphaned
      // redraw frame after `map.remove()` drops the 2d context (GPX-37).
    },
  }
}
